import Component from '@glimmer/component';
import { eq } from 'tio-ui/utilities';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { Section } from 'tio-reimagined/components/layout';
import { PhCheckCircle, PhXCircle } from 'ember-phosphor-icons';
import { t } from 'ember-intl';
import type { StepDefinition } from './wizard';

interface ProgressTrackerSignature {
  Args: {
    steps: StepDefinition[];
    currentStepIndex: number;
    furthestVisitedStepIndex?: number;
    onGoToStep?: (index: number) => void;
  };
  Element: HTMLDivElement;
}

export default class ProgressTracker extends Component<ProgressTrackerSignature> {
  handleGoToStep = (index: number) => {
    this.args.onGoToStep?.(index);
  };

  isPastStep = (index: number) => {
    return index < this.args.currentStepIndex;
  };

  isAccessibleStep = (index: number) => {
    const furthestVisitedStepIndex =
      this.args.furthestVisitedStepIndex ?? this.args.currentStepIndex;

    return index !== this.args.currentStepIndex && index <= furthestVisitedStepIndex;
  };

  stepStatus = (index: number) => {
    const furthestVisitedStepIndex =
      this.args.furthestVisitedStepIndex ?? this.args.currentStepIndex;
    const isVisitedStep =
      index < this.args.currentStepIndex ||
      (index > this.args.currentStepIndex && index <= furthestVisitedStepIndex);

    if (isVisitedStep) {
      if (this.args.steps[index]?.isComplete ?? false) {
        return 'completed';
      }
      return 'incomplete';
    }
    if (index === this.args.currentStepIndex) {
      return 'current';
    }
    return 'upcoming';
  };

  <template>
    <Section ...attributes>
      <:header>
        <span class="text-sm font-semibold tracking-wide uppercase text-gray-500">{{t
            "tas.guided.progress"
          }}</span>
      </:header>
      <:body>
        <nav aria-label={{t "tas.guided.application_progress"}} class="py-2">
          <ol class="relative">
            {{#each @steps as |step index|}}
              {{#let (this.stepStatus index) as |status|}}
                <li
                  class="relative flex items-start gap-3 pb-6 last:pb-0"
                  aria-current={{if (eq status "current") "step"}}
                >
                  {{! Connecting line }}
                  {{#if (this.isNotLastStep index)}}
                    <div
                      class="absolute left-3.25 top-7 w-0.5 h-[calc(100%-12px)]
                        {{if
                          (eq status 'completed')
                          'bg-teal-500'
                          (if (eq status 'incomplete') 'bg-error-400' 'bg-gray-200')
                        }}"
                    ></div>
                  {{/if}}

                  {{! Step indicator }}
                  {{#if (eq status "completed")}}
                    <span
                      class="relative shrink-0 z-10 flex items-center justify-center w-7 h-7 rounded-full bg-white"
                    >
                      <PhCheckCircle @size="28" @weight="fill" class="text-teal-500" />
                    </span>
                  {{else if (eq status "incomplete")}}
                    <span
                      class="relative shrink-0 z-10 flex items-center justify-center w-7 h-7 rounded-full bg-error-50 ring-2 ring-error-100"
                    >
                      <PhXCircle @size="28" @weight="fill" class="text-error-600" />
                    </span>
                  {{else if (eq status "current")}}
                    <span
                      class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-violet-600 text-white text-xs font-bold shrink-0 z-10"
                    >{{this.displayNumber index}}</span>
                  {{else}}
                    <span
                      class="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-gray-300 text-gray-400 text-xs font-medium shrink-0 z-10 bg-white"
                    >{{this.displayNumber index}}</span>
                  {{/if}}

                  {{! Step label + status }}
                  {{#if (this.isAccessibleStep index)}}
                    <button
                      type="button"
                      class="flex flex-col min-w-0 text-left cursor-pointer group"
                      {{on "click" (fn this.handleGoToStep index)}}
                    >
                      <span
                        class="text-sm leading-tight group-hover:text-violet-600 transition-colors
                          {{if
                            (eq status 'completed')
                            'font-medium text-gray-700'
                            (if
                              (eq status 'incomplete')
                              'font-medium text-error-700'
                              'font-medium text-gray-700'
                            )
                          }}"
                      >{{step.label}}</span>
                      <span
                        class="text-xs mt-0.5
                          {{if
                            (eq status 'completed')
                            'text-teal-600'
                            (if (eq status 'incomplete') 'text-error-400' 'text-gray-400')
                          }}"
                      >
                        {{#if (eq status "completed")}}
                          {{t "tas.guided.steps.complete"}}
                        {{else if (eq status "incomplete")}}
                          {{t "tas.guided.steps.incomplete"}}
                        {{/if}}
                      </span>
                    </button>
                  {{else}}
                    <div class="flex flex-col min-w-0">
                      <span
                        class="text-sm leading-tight
                          {{if
                            (eq status 'current')
                            'font-semibold text-gray-900'
                            'text-gray-400'
                          }}"
                      >{{step.label}}</span>

                    </div>
                  {{/if}}
                </li>
              {{/let}}
            {{/each}}
          </ol>
        </nav>
      </:body>
    </Section>
  </template>

  displayNumber = (index: number) => {
    return index + 1;
  };

  isNotLastStep = (index: number) => {
    return index !== this.args.steps.length - 1;
  };
}
